import { createTheme } from '@mui/material/styles';
import backgroundNoise from '../assets/patterns/background_noise.png';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#394b57',
      light: '#3c5462',
      dark: '#2b3942',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00a779',
      light: '#20e6a8',
      dark: '#00845f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F6F6F6',
    },
    text: {
      primary: '#394b57',
      secondary: '#3c5462',
      disabled: '#b6c1c3',
    },
    spruce: {
      50: '#F3F7F8',
      100: '#DFEAEE',
      '200Base': '#C3D7DE',
      300: '#9ABAC6',
      400: '#6994A7',
      500: '#4E798C',
      600: '#446576',
      700: '#3C5462',
      '800Base': '#394B57', // text.primary
      900: '#313E48',
      950: '#1D272F',
    },
    iron: {
      50: '#F6F7F8',
      100: '#EBECEE',
      '200Base': '#D8DCDF',
      300: '#C3C9CD',
      400: '#A5AEB5',
      500: '#8F98A2',
      600: '#7E8792',
      700: '#717884',
      800: '#5F656E',
      900: '#4F5259',
      950: '#323539',
    },
    shamrock: {
      50: '#EAFFF6',
      100: '#CCFFE8',
      200: '#9EFCD6',
      300: '#5FF6C2',
      '400Base': '#20E6A8',
      500: '#00CD93',
      600: '#00A779',
      700: '#008664',
      800: '#006A50',
      900: '#005743',
    },
    porcelain: {
      50: '#F7F8F8',
      '100Base': '#E9ECED',
      200: '#D8DEDF',
      300: '#B6C1C3',
      400: '#8E9FA2',
      500: '#708387',
      600: '#5A6B6F',
      700: '#4A565A',
    },
    cranberry: {
      300: '#F8A9BC',
      400: '#F3799A',
      '500Base': '#EA4C7A',
      600: '#D62863',
      700: '#B41C53',
      800: '#971A4C',
      900: '#811A46',
    },
    oxygen: {
      oxygenColor: '#2C0DF1',
    },
  },

  // Typography
  typography: {
    fontFamily: 'Inter',
    fontSize: 12,
    button: {
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: 300,
      textTransform: 'none',
      lineHeight: 1,
    },
    caption: {
      fontFamily: 'Inter',
      fontSize: '10px',
      fontWeight: 400,
      color: '#3C5462',
    },
  },

  // MUI Components
  components: {
    // MUICssBaseline Override (application background)
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // backgroundColor: '#F6F6F6',
          backgroundColor: '#D8DCDF',
          backgroundImage: `url(${backgroundNoise})`,
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
          backgroundAttachment: 'fixed',
        },
      },
    },

    // MUIButton Override
    MuiButton: {
      defaultProps: {
        variant: 'text',
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => {
          return {
            position: 'relative',
            color: theme.palette.text.primary,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 400,
            minWidth: 'auto',
            padding: '10px 12px',
            '&:before': {},
            '&:hover': {
              backgroundColor: theme.palette.porcelain[200],
            },
            '&:active': {},
            '&.Mui-disabled': {},
          };
        },
        contained: {},
        outlined: {},
      },
      variants: [
        {
          props: {
            variant: 'documentation',
          },
          style: ({ theme }) => ({
            color: theme.palette.spruce['800Base'],
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            textDecorationColor: theme.palette.spruce['800Base'],
            textDecorationThickness: '1px',
            textDecorationStyle: 'solid',
            textDecorationSkipInk: 'none',
            padding: '0px',
            '& .MuiButton-startIcon': {
              marginRight: '8px',
              marginLeft: '0px',
            },
            '&:hover': {
              backgroundColor: 'transparent',
              color: theme.palette.spruce[950],
              textDecoration: 'underline',
            },
          }),
        },
      ],
    },

    // Menu lists and list items

    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          minWidth: 180,
          backgroundColor: theme.palette.porcelain['100Base'],
          boxShadow: `0 4px 12px ${theme.palette.iron['200Base']}`,
          border: `1px solid ${theme.palette.iron['200Base']}`,
          borderRadius: 4,
        }),
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {},
      },
    },

    MuiListItemText: {
      defaultProps: {
        slotProps: {
          primary: {
            fontSize: '12px',
          },
        },
      },
      variants: [
        {
          props: {
            variant: 'transitionListItem',
          },
          style: ({ theme }) => ({
            color: theme.palette.spruce['800Base'],
          }),
        },
      ],
    },

    MuiDivider: { styleOverrides: { root: { marginTop: 20, marginBottom: 20 } } },

    // Selects
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: '#F9FAFA',
          borderRadius: 4,
          border: `1px solid ${theme.palette.iron['200Base']}`,
          boxShadow: '0 -1px 0 0 rgba(0,0,0,0.2), 0 1px 0 0 #ffffff',
          paddingLeft: '8px',
        }),
        icon: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
      defaultProps: {
        size: 'small',
      },
    },

    // Radio buttons
    MuiRadio: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          padding: '4px',
          paddingTop: '0px',
          '& .MuiSvgIcon-root': {
            fontSize: 16,
          },
          color: theme.palette.iron[500],
          '&.Mui-checked': {
            color: ownerState.color?.startsWith?.('#') ? ownerState.color : theme.palette.iron[500],
          },
        }),
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
        },
        label: ({ theme }) => ({
          fontSize: '12px',
          fontWeight: 400,
          color: theme.palette.spruce['800Base'],
          lineHeight: 1.2,
        }),
      },
    },
  },
});

export default theme;
